import React, { useState } from 'react';
import Modal from '../../../ui/Modal';
import Button from '../../../ui/Button';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { orderItemApi } from '../../../../redux/apis/endpoints/orderItemApi';
import { fulfillmentGroupApi } from '../../../../redux/apis/endpoints/fulfillmentGroupApi';
import { requestApi } from '../../../../redux/apis/endpoints/requestApi';
import { useDispatch } from 'react-redux';
import ReturnItemInfo from '../ReturnItemInfo';

const RequestGroupReturnModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  entity 
}) => {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [returnReason, setReturnReason] = useState('');
  const [returnComments, setReturnComments] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  // RTK Query hooks - only need group return request
  const [submitGroupReturnRequest] = requestApi.useSubmitGroupReturnRequestMutation();

  const orderItems = entity?.orderItems || [];
  const returnableItems = orderItems.filter(item => 
    ['delivered', 'pickup_completed_by_customer'].includes(item.status)
  );

  const handleItemToggle = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === returnableItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(returnableItems.map(item => item.id)));
    }
  };

  const handleConfirm = async () => {
    if (selectedItems.size === 0) {
      alert('Please select at least one item to return');
      return;
    }

    if (!returnReason) {
      alert('Please provide a reason for the return');
      return;
    }

    setIsLoading(true);

    try {
      const itemIds = Array.from(selectedItems);
      
      console.log('🔄 Creating single group return request:', {
        selectedItemIds: itemIds,
        returnReason,
        returnComments,
        fulfillmentGroup: entity
      });

      // Always use group return request - create one request with multiple order items
      const groupRequestData = {
        fulfillmentGroupId: entity?.id,
        orderItemIds: itemIds, // Pass all selected item IDs
          reason: returnReason,
          comments: returnComments
        };
        
      // Optimistic update: Update the fulfillment group and order items immediately
      const fulfillmentGroupId = entity?.id;
      
      // Optimistically update fulfillment group status
      if (fulfillmentGroupId) {
        dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, (draft) => {
            if (draft) {
              draft.status = 'return_requested';
              // Update selected order items status
              if (draft.orderItems) {
                draft.orderItems.forEach(item => {
                  if (itemIds.includes(item.id)) {
                    item.status = 'return_requested_by_customer';
                  }
                });
              }
            }
          })
        );
      }

      // Optimistically update individual order item queries
      itemIds.forEach(itemId => {
        dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', itemId, (draft) => {
            if (draft) {
              draft.status = 'return_requested_by_customer';
            }
          })
        );
      });

      const results = await submitGroupReturnRequest(groupRequestData).unwrap();
      console.log('✅ Group return request created successfully:', results);

      // Enhanced cache invalidation to trigger immediate UI updates
      const tags = [
        { type: 'OrderItem', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'Request', id: 'LIST' }
      ];
      
      // Add specific fulfillment group tag if we have the ID
      if (fulfillmentGroupId) {
        tags.push({ type: 'FulfillmentGroup', id: fulfillmentGroupId });
      }
      
      // Add specific order item tags for the updated items
      itemIds.forEach(itemId => {
        tags.push({ type: 'OrderItem', id: itemId });
      });
      
      console.log('🔄 Invalidating cache tags for return requests:', tags);
      
      dispatch(orderItemApi.util.invalidateTags(tags));

      const successMessage = `Return request created for ${itemIds.length} item(s)`;

      onConfirm({
        success: true,
        message: successMessage,
        results
      });

      onClose();
    } catch (error) {
      console.error('❌ Failed to create return request:', {
        error,
        errorMessage: error?.message,
        errorData: error?.data,
        status: error?.status
      });
      
      // Revert optimistic updates on error
      const fulfillmentGroupId = entity?.id;
      const itemIds = Array.from(selectedItems);
      
      if (fulfillmentGroupId) {
        dispatch(
          fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, (draft) => {
            if (draft) {
              // Revert to original status - assume it was delivered
              draft.status = 'delivery_completed_to_customer';
              if (draft.orderItems) {
                draft.orderItems.forEach(item => {
                  if (itemIds.includes(item.id)) {
                    item.status = 'delivered'; // Revert to delivered status
                  }
                });
              }
            }
          })
        );
      }

      itemIds.forEach(itemId => {
        dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', itemId, (draft) => {
            if (draft) {
              draft.status = 'delivered'; // Revert to delivered status
            }
          })
        );
      });
      
    onConfirm({ 
        success: false,
        message: error?.data?.message || error?.message || 'Failed to create return request'
    });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedItems(new Set());
    setReturnReason('');
    setReturnComments('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Request Group Return"
      size="lg"
    >
      <div className="py-4">
        <div className="flex items-center mb-4">
          <ArrowUturnLeftIcon className="h-12 w-12 text-blue-500 mr-4" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Request Return for Items
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Select the items you'd like to return and provide a reason.
            </p>
          </div>
        </div>

        {returnableItems.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No items are eligible for return.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Item Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select items to return
                </label>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-sm text-brand-teal hover:text-brand-teal-dark"
                >
                  {selectedItems.size === returnableItems.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-60 overflow-y-auto">
                {returnableItems.map((item) => (
                  <div key={item.id} className="p-3">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => handleItemToggle(item.id)}
                        className="mt-1 h-4 w-4 text-brand-teal focus:ring-brand-teal border-gray-300 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <ReturnItemInfo item={item} />
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Return Reason */}
            <div>
              <label htmlFor="return-reason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for return *
              </label>
              <select
                id="return-reason"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                required
              >
                <option value="">Select a reason</option>
                <option value="defective">Item is defective</option>
                <option value="wrong_size">Wrong size</option>
                <option value="not_as_described">Not as described</option>
                <option value="changed_mind">Changed my mind</option>
                <option value="damaged_shipping">Damaged during shipping</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Return Comments */}
            <div>
              <label htmlFor="return-comments" className="block text-sm font-medium text-gray-700 mb-2">
                Additional details (optional)
              </label>
              <textarea
                id="return-comments"
                value={returnComments}
                onChange={(e) => setReturnComments(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                placeholder="Please provide any additional details about your return request..."
              />
            </div>
          </div>
        )}

        {selectedItems.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 mt-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              Return Process:
            </h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Return requests require merchant approval.</li>
              <li>If <span className="font-semibold">approved</span>, you pay a <span className="font-semibold">usage fee</span> for time used <em>plus</em> return shipping.</li>
              <li>The merchant may <span className="font-semibold">decline</span> your request.</li>
             <li>Refund will be processed after items are received and inspected.</li>
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row-reverse gap-3">
          <Button
            onClick={handleConfirm}
            disabled={isLoading || selectedItems.size === 0 || !returnReason}
            isLoading={isLoading}
            variant="primary"
            className="flex items-center justify-center"
          >
            <ArrowUturnLeftIcon className="h-4 w-4 mr-2" />
            Request Return ({selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''})
          </Button>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            variant="secondary"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RequestGroupReturnModal; 